import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTheme } from "../../contexts/ThemeContext";
import { useDistributionAggregatePresence } from "../../hooks/useDistributionAggregatePresence";
import { EmptyState } from "./EmptyState";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface PresencePoint {
  time: string;
  value: number;
}

function mapApiToPresencePoint(points: [string, number][]): PresencePoint[] {
  return points.map(([time, value]) => ({ time, value }));
}

const CHART_CARD_CLASS_DARK = "bg-[#1F2937] border border-[#374151] shadow-lg rounded-2xl";
const CHART_CARD_CLASS_LIGHT = "bg-white border border-gray-200 shadow-lg rounded-2xl";
const CHART_HEIGHT = 300;

const STUDENT_LINE_COLOR = "#14B8A6";

export interface FilterParams {
  location_id?: string;
  camera_id?: string;
  from?: string;
  to?: string;
  timezone?: string;
}

const StudentAggregatePresenceChart: React.FC<{
  filterParams?: FilterParams;
  distributionType?: string;
  /** When false, no distribution API requests are made (wire your own data layer later). */
  apisEnabled?: boolean;
}> = ({ filterParams, distributionType, apisEnabled = true }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const chartCardClass = isDarkMode ? CHART_CARD_CLASS_DARK : CHART_CARD_CLASS_LIGHT;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: apiData = [], isLoading, isError, usingWebSocketData } = useDistributionAggregatePresence({
    from: filterParams?.from,
    to: filterParams?.to,
    location_id: filterParams?.location_id,
    camera_id: filterParams?.camera_id,
    type: distributionType ?? "student",
    enabled: apisEnabled,
  });
  const data = mapApiToPresencePoint(apiData);
  const useWsForDisplay = usingWebSocketData;

  const chartData = useMemo(() => {
    if (apiData.length > 0 && Array.isArray(apiData[0])) {
      return apiData;
    }
    return data.map((point) => [new Date(point.time).getTime(), point.value]);
  }, [apiData, data]);

  const values = chartData.map((item: unknown) => (item as [number, number])[1]);
  const dataMax = Math.max(0, ...values);
  const peakIndex = values.findIndex((v) => v === dataMax);
  const peakPoint = peakIndex >= 0 ? chartData[peakIndex] : null;

  const getNiceYAxis = () => {
    if (dataMax === 0) return { min: 0, max: 10 };
    const step = dataMax <= 10 ? 5 : dataMax <= 50 ? 10 : dataMax <= 200 ? 20 : 50;
    const max = Math.ceil((dataMax * 1.05) / step) * step;
    return { min: 0, max: Math.max(max, step) };
  };
  const yAxisScale = getNiceYAxis();

  if (!useWsForDisplay && isLoading) {
    return (
      <div className={`${chartCardClass} p-4 sm:p-6`}>
        <div className="flex items-center gap-3 mb-4">
          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Loading chart…</p>
        </div>
        <div className={`h-[280px] sm:h-[320px] rounded-xl animate-pulse ${isDarkMode ? "bg-gray-700/50" : "bg-gray-200"}`} />
      </div>
    );
  }

  if (!useWsForDisplay && isError) {
    return (
      <div className={`${chartCardClass} p-4 sm:p-6`}>
        <p className="text-red-500 dark:text-red-400 text-sm">Failed to load student aggregate presence.</p>
      </div>
    );
  }

  const hasNoData = chartData.length === 0;
  if (hasNoData) {
    return (
      <div className={`${chartCardClass} p-4 sm:p-6 min-h-[280px] sm:min-h-[320px] flex items-center justify-center`}>
        <EmptyState
          variant="chart"
          title={apisEnabled ? "No student presence data" : "Chart API not wired"}
          description={
            apisEnabled
              ? "No aggregate presence recorded for the selected period. Data will appear once cameras start reporting."
              : "Enable distribution aggregate-presence calls or plug in your own series when ready."
          }
          isDarkMode={isDarkMode}
        />
      </div>
    );
  }

  const labelColor = isDarkMode ? "#9CA3AF" : "#6B7280";
  const gridColor = isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)";
  const tooltipBg = isDarkMode ? "rgba(31, 41, 55, 0.95)" : "rgba(255, 255, 255, 0.98)";
  const tooltipBorder = isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)";
  const totalPoints = chartData.length;
  const pointsToShow = Math.min(96, totalPoints);
  const startIndex = Math.max(0, totalPoints - pointsToShow);
  const startPercent = (startIndex / totalPoints) * 100;
  const endPercent = 100;

  const option = {
    tooltip: {
      trigger: "axis",
      backgroundColor: tooltipBg,
      borderColor: tooltipBorder,
      textStyle: { color: isDarkMode ? "#fff" : "#111827", fontSize: 11 },
      formatter: (params: unknown) => {
        const p = params as { value: [number, number] }[];
        if (!p || !p.length) return "";
        const d = p[0];
        const date = dayjs(d.value[0]).tz(tz).format("DD MMM YYYY, h:mm:ss A");
        return `${date}<br/><span style="color:${STUDENT_LINE_COLOR}">●</span> Student: ${d.value[1]}`;
      },
    },
    dataZoom: [
      {
        type: "inside",
        start: startPercent,
        end: endPercent,
        zoomOnMouseWheel: true,
        moveOnMouseWheel: true,
        moveOnMouseMove: true,
        zoomLock: false,
      },
      {
        type: "slider",
        start: startPercent,
        end: endPercent,
        height: 20,
        bottom: 8,
        borderColor: "transparent",
        fillerColor: isDarkMode ? "rgba(20, 184, 166, 0.3)" : "rgba(20, 184, 166, 0.25)",
        handleStyle: {
          color: STUDENT_LINE_COLOR,
          borderColor: STUDENT_LINE_COLOR,
        },
        textStyle: { color: labelColor, fontSize: 10 },
        dataBackground: {
          lineStyle: { color: "transparent" },
          areaStyle: {
            color: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          },
        },
      },
    ],
    grid: {
      left: "5%",
      right: "4%",
      bottom: "18%",
      top: "8%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        rotate: 45,
        margin: 10,
        formatter: (value: string) => dayjs(value).tz(tz).format("D MMM h:mmA"),
      },
      splitLine: { show: true, lineStyle: { color: gridColor, type: "dotted" } },
      minInterval: 15 * 60 * 1000,
    },
    yAxis: {
      type: "value",
      min: yAxisScale.min,
      max: yAxisScale.max,
      splitNumber: 5,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: labelColor, fontSize: 10 },
      splitLine: { show: true, lineStyle: { color: gridColor, type: "dotted" } },
    },
    legend: {
      show: true,
      bottom: 4,
      left: "center",
      textStyle: { color: labelColor, fontSize: 10 },
      itemGap: 8,
    },
    series: [
      {
        name: "Student presence",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 8,
        showSymbol: dataMax > 0,
        data: chartData,
        lineStyle: { color: STUDENT_LINE_COLOR, width: 2 },
        itemStyle: { color: STUDENT_LINE_COLOR, borderWidth: 0 },
        areaStyle:
          dataMax > 0
            ? {
                color: STUDENT_LINE_COLOR,
                opacity: 0.1,
              }
            : undefined,
        markPoint: peakPoint && dataMax > 0
          ? {
              symbol: "pin",
              symbolSize: 40,
              data: [
                {
                  coord: peakPoint as [number, number],
                  value: Math.round((peakPoint as [number, number])[1]),
                  itemStyle: { color: STUDENT_LINE_COLOR },
                  label: {
                    show: true,
                    formatter: "Peak: {c}",
                    color: isDarkMode ? "#fff" : "#111827",
                  },
                },
              ],
            }
          : undefined,
      },
    ],
  };

  return (
    <div className={`${chartCardClass} p-4 sm:p-6 transition-all duration-200 hover:shadow-xl`}>
      <ReactECharts
        option={option}
        style={{ height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}
        opts={{ renderer: "canvas" }}
        key={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default StudentAggregatePresenceChart;
